// /lib/errors/availability.ts

import { ApiError, toApiError } from './apiError'

/**
 * Error codes the API returns when a session can no longer take the requested
 * party size, or isn't on sale (a pre-sale date outside its windows).
 *
 * The seat counts on the sessions list aren't reliable (that response can be
 * cached), so the cart-create response is the only trustworthy signal that a
 * session is full. The same business failure can come back under different
 * HTTP statuses depending on which layer of the API caught it, so match on the
 * code and ignore the status.
 */
const NO_AVAILABILITY_CODES = [
  'NO_AVAILABILITY',
  'INSUFFICIENT_AVAILABILITY',
  'SESSION_NOT_ON_SALE',
]

export function isNoAvailabilityError(error: unknown): boolean {
  const apiError = error instanceof ApiError ? error : toApiError(error)
  const code = apiError.data?.errorCode ?? apiError.data?.code ?? apiError.code

  return typeof code === 'string' && NO_AVAILABILITY_CODES.includes(code)
}

/**
 * Thrown when the session the user picked can't be booked. Carries the session
 * id so the stage can de-select it and mark it as sold out.
 */
export class SessionUnavailableError extends Error {
  readonly sessionId: string

  constructor(sessionId: string) {
    super(`Session ${sessionId} has no availability`)
    this.name = 'SessionUnavailableError'
    this.sessionId = sessionId
  }
}
